"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import IndexBadges from "@/components/IndexBadges";

type RsiRow = {
  symbol: string;
  name: string;
  sector: string;
  close: number;
  date: string;
  rsi_prev: number;
  rsi_curr: number;
  volume?: number;
  indices?: string[];
};

type MacdRow = {
  symbol: string;
  name: string;
  sector: string;
  close: number;
  date: string;
  hist_prev: number | null;
  hist_curr: number | null;
  bearish_days?: number | null;
  indices?: string[];
};

type PulseRow = {
  symbol: string;
  name: string;
  sector: string;
  close: number;
  date: string;
  hasRsi: boolean;
  hasMacd: boolean;
  rsiPrev?: number;
  rsiCurr?: number;
  histPrev?: number | null;
  histCurr?: number | null;
  bearishDays?: number | null;
  conviction: number;
  score: number;
  indices: string[];
};

type Mode = "all" | "any";

const RSI_LEVELS = [30, 50, 60] as const;
const MODE_OPTIONS: Array<{ value: Mode; label: string; note: string }> = [
  { value: "all", label: "All", note: "Only show names where RSI and MACD agree." },
  { value: "any", label: "Any", note: "Show names where either momentum trigger is active." },
];

const formatNumber = (value: number | null | undefined, digits = 2) =>
  typeof value === "number" && Number.isFinite(value)
    ? value.toLocaleString(undefined, { maximumFractionDigits: digits })
    : "—";

const formatSigned = (value: number | null | undefined, digits = 2) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(digits)}`;
};

export default function ScannerPulsePage() {
  const [mode, setMode] = useState<Mode>("all");
  const [rsiLevel, setRsiLevel] = useState<number>(50);
  const [fast, setFast] = useState<number>(12);
  const [slow, setSlow] = useState<number>(26);
  const [signal, setSignal] = useState<number>(9);
  const [rsiRows, setRsiRows] = useState<RsiRow[]>([]);
  const [macdRows, setMacdRows] = useState<MacdRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const isDefaultRsi = rsiLevel === 50;
        const isDefaultMacd = fast === 12 && slow === 26 && signal === 9;
        const [rsiJson, macdJson] = await Promise.all([
          isDefaultRsi
            ? fetch("/api/outputs/scanner_rsi.json").then(r => r.json() as Promise<{ rows?: RsiRow[] }>)
            : api.get<{ rows?: RsiRow[] }>("/api/scanner/rsi-cross", { level: rsiLevel, limit: 60 }),
          isDefaultMacd
            ? fetch("/api/outputs/scanner_macd.json").then(r => r.json() as Promise<{ rows?: MacdRow[] }>)
            : api.get<{ rows?: MacdRow[] }>("/api/scanner/macd-turn", { fast, slow, signal, limit: 60 }),
        ]);

        setRsiRows(Array.isArray(rsiJson.rows) ? rsiJson.rows : []);
        setMacdRows(Array.isArray(macdJson.rows) ? macdJson.rows : []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load pulse view.");
        setRsiRows([]);
        setMacdRows([]);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [fast, rsiLevel, signal, slow]);

  const pulseRows = useMemo(() => {
    const rsiMap = new Map(rsiRows.map((row) => [row.symbol, row]));
    const macdMap = new Map(macdRows.map((row) => [row.symbol, row]));
    const symbols =
      mode === "all"
        ? Array.from(rsiMap.keys()).filter((symbol) => macdMap.has(symbol))
        : Array.from(new Set([...rsiMap.keys(), ...macdMap.keys()]));

    return symbols
      .map((symbol) => {
        const rsi = rsiMap.get(symbol);
        const macd = macdMap.get(symbol);
        const rsiDelta =
          typeof rsi?.rsi_curr === "number" && typeof rsi?.rsi_prev === "number"
            ? rsi.rsi_curr - rsi.rsi_prev
            : 0;
        const macdShift =
          typeof macd?.hist_curr === "number" && typeof macd?.hist_prev === "number"
            ? macd.hist_curr - macd.hist_prev
            : 0;

        return {
          symbol,
          name: rsi?.name || macd?.name || symbol,
          sector: rsi?.sector || macd?.sector || "—",
          close: rsi?.close ?? macd?.close ?? 0,
          date: rsi?.date || macd?.date || "—",
          hasRsi: Boolean(rsi),
          hasMacd: Boolean(macd),
          rsiPrev: rsi?.rsi_prev,
          rsiCurr: rsi?.rsi_curr,
          histPrev: macd?.hist_prev,
          histCurr: macd?.hist_curr,
          bearishDays: macd?.bearish_days,
          conviction: Number(Boolean(rsi)) + Number(Boolean(macd)),
          score: Math.abs(rsiDelta) + Math.abs(macdShift) * 100,
          indices: rsi?.indices ?? macd?.indices ?? [],
        } satisfies PulseRow;
      })
      .sort((left, right) => right.conviction - left.conviction || right.score - left.score)
      .slice(0, 60);
  }, [macdRows, mode, rsiRows]);

  const summary = useMemo(() => {
    const highConviction = pulseRows.filter((row) => row.conviction === 2).length;
    const rsiOnly = pulseRows.filter((row) => row.hasRsi && !row.hasMacd).length;
    const macdOnly = pulseRows.filter((row) => row.hasMacd && !row.hasRsi).length;
    return {
      total: pulseRows.length,
      highConviction,
      rsiOnly,
      macdOnly,
    };
  }, [pulseRows]);

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">Momentum Pulse</h1>
          <p className="page-subtitle">
            Collapse RSI and MACD into one convergence view so momentum confirmation reads like a setup stack, not two isolated indicator pages.
          </p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          <div
            style={{
              borderRadius: "18px",
              border: "1px solid rgba(59, 130, 246, 0.22)",
              background:
                "radial-gradient(circle at top left, rgba(59, 130, 246, 0.14), transparent 44%), linear-gradient(155deg, rgba(15, 23, 42, 0.94), rgba(10, 16, 26, 0.9))",
              padding: "1.3rem",
              marginBottom: "1.25rem",
            }}
          >
            <div style={{ display: "grid", gap: "0.65rem" }}>
              <div style={{ fontSize: "0.76rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#93c5fd", fontWeight: 700 }}>
                Convergence Logic
              </div>
              <h2 style={{ margin: 0, fontSize: "1.45rem", color: "#f8fafc" }}>Use momentum as a filter stack, not a page maze.</h2>
              <p style={{ margin: 0, color: "#94a3b8", lineHeight: 1.75, maxWidth: 920 }}>
                Professional scanners do not force traders to cross-reference multiple tabs for the same decision. This workspace shows where RSI reclaim and MACD histogram expansion are aligned, then lets you loosen the rule with `Any` mode only when signal breadth matters more than precision.
              </p>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "0.85rem",
              marginBottom: "1rem",
            }}
          >
            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>Pulse matches</div>
              <div style={summaryValueStyle}>{summary.total}</div>
              <p style={summaryCopyStyle}>Names returned by the active convergence rule.</p>
            </div>
            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>High conviction</div>
              <div style={summaryValueStyle}>{summary.highConviction}</div>
              <p style={summaryCopyStyle}>Symbols where RSI and MACD are both confirming.</p>
            </div>
            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>RSI only</div>
              <div style={summaryValueStyle}>{summary.rsiOnly}</div>
              <p style={summaryCopyStyle}>Early momentum names without MACD confirmation yet.</p>
            </div>
            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>MACD only</div>
              <div style={summaryValueStyle}>{summary.macdOnly}</div>
              <p style={summaryCopyStyle}>Histogram turns that have not reclaimed RSI yet.</p>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "0.8rem",
              marginBottom: "1.4rem",
              borderRadius: "16px",
              border: "1px solid rgba(148, 163, 184, 0.18)",
              background: "rgba(15, 23, 42, 0.56)",
              padding: "1rem",
            }}
          >
            <div>
              <label style={controlLabelStyle}>Logic</label>
              <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap" }}>
                {MODE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setMode(option.value)}
                    style={modeChipStyle(mode === option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <div style={controlNoteStyle}>{MODE_OPTIONS.find((option) => option.value === mode)?.note}</div>
            </div>

            <div>
              <label style={controlLabelStyle}>RSI reclaim level</label>
              <select value={rsiLevel} onChange={(e) => setRsiLevel(Number(e.target.value))} style={controlInputStyle}>
                {RSI_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    RSI {level}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={controlLabelStyle}>MACD fast</label>
              <input type="number" min={2} max={50} value={fast} onChange={(e) => setFast(Number(e.target.value) || 12)} style={controlInputStyle} />
            </div>

            <div>
              <label style={controlLabelStyle}>MACD slow</label>
              <input type="number" min={5} max={100} value={slow} onChange={(e) => setSlow(Number(e.target.value) || 26)} style={controlInputStyle} />
            </div>

            <div>
              <label style={controlLabelStyle}>MACD signal</label>
              <input type="number" min={2} max={50} value={signal} onChange={(e) => setSignal(Number(e.target.value) || 9)} style={controlInputStyle} />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              marginBottom: "1rem",
              flexWrap: "wrap",
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: "1.08rem" }}>Current setup stack</h2>
              <p style={{ margin: "0.35rem 0 0", color: "var(--color-text-muted)" }}>
                Start with the `All` list for high-conviction names. Use `Any` only when you want breadth after market context is already clear.
              </p>
            </div>
            <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
              <Link className="ghostButton" href="/scanner/rsi">
                Open RSI
              </Link>
              <Link className="ghostButton" href="/scanner/macd">
                Open MACD
              </Link>
            </div>
          </div>

          <div className="results-table" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.03)", borderBottom: "1px solid var(--color-border)" }}>
                  <th style={thLeft}>Symbol</th>
                  <th style={thLeft}>Sector</th>
                  <th style={thRight}>Close</th>
                  <th style={thLeft}>Setup stack</th>
                  <th style={thRight}>RSI move</th>
                  <th style={thRight}>MACD shift</th>
                  <th style={thLeft}>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} style={emptyRowStyle}>
                      Loading momentum pulse...
                    </td>
                  </tr>
                )}
                {!loading && error && (
                  <tr>
                    <td colSpan={7} style={{ ...emptyRowStyle, color: "#fca5a5" }}>
                      {error}
                    </td>
                  </tr>
                )}
                {!loading && !error && pulseRows.length === 0 && (
                  <tr>
                    <td colSpan={7} style={emptyRowStyle}>
                      {mode === "all" && (rsiRows.length > 0 || macdRows.length > 0)
                        ? `No overlap between RSI ${rsiLevel} crossovers (${rsiRows.length}) and MACD turns (${macdRows.length}). Switch to "Any" to see partial signals.`
                        : "No symbols match the current pulse logic."}
                    </td>
                  </tr>
                )}
                {!loading &&
                  !error &&
                  pulseRows.map((row, index) => {
                    const rsiShift =
                      typeof row.rsiCurr === "number" && typeof row.rsiPrev === "number"
                        ? row.rsiCurr - row.rsiPrev
                        : null;
                    const tone = row.conviction === 2 ? "var(--color-success)" : row.hasRsi ? "#38bdf8" : "#f59e0b";

                    return (
                      <tr
                        key={`${row.symbol}-${index}`}
                        style={{
                          borderBottom: "1px solid rgba(148, 163, 184, 0.12)",
                          background: index % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent",
                        }}
                      >
                        <td style={{ padding: "0.95rem 1rem", fontWeight: 700, color: "#f8fafc" }}>
                          {row.symbol}
                          <IndexBadges indices={row.indices} style={{ marginLeft: 5, verticalAlign: "middle" }} />
                        </td>
                        <td style={{ padding: "0.95rem 1rem", color: "var(--color-text-muted)" }}>{row.sector}</td>
                        <td style={{ padding: "0.95rem 1rem", textAlign: "right" }} className="tabular-nums">
                          {formatNumber(row.close)}
                        </td>
                        <td style={{ padding: "0.95rem 1rem" }}>
                          <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap" }}>
                            <span style={stackPillStyle(row.hasRsi, "#38bdf8")}>RSI</span>
                            <span style={stackPillStyle(row.hasMacd, "var(--color-success)")}>MACD</span>
                            <span style={{ color: tone, fontWeight: 700, fontSize: "0.82rem" }}>
                              {row.conviction === 2 ? "High conviction" : "Partial confirmation"}
                            </span>
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "0.95rem 1rem",
                            textAlign: "right",
                            color: row.hasRsi ? "var(--color-success)" : "var(--color-text-muted)",
                            fontWeight: 700,
                          }}
                          className="tabular-nums"
                        >
                          {row.hasRsi ? `${formatNumber(row.rsiPrev, 1)} → ${formatNumber(row.rsiCurr, 1)} (${formatSigned(rsiShift, 1)})` : "—"}
                        </td>
                        <td
                          style={{
                            padding: "0.95rem 1rem",
                            textAlign: "right",
                            color: row.hasMacd ? "var(--color-success)" : "var(--color-text-muted)",
                            fontWeight: 700,
                          }}
                          className="tabular-nums"
                        >
                          {row.hasMacd ? `${formatNumber(row.histPrev, 3)} → ${formatNumber(row.histCurr, 3)}` : "—"}
                        </td>
                        <td style={{ padding: "0.95rem 1rem", color: "var(--color-text-muted)" }}>{row.date}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}

const summaryCardStyle = {
  borderRadius: "12px",
  padding: "1rem 1.1rem",
  border: "1px solid var(--color-border)",
  background: "rgba(255,255,255,0.03)",
} as const;

const summaryLabelStyle = {
  color: "var(--color-text-muted)",
  fontSize: "0.76rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  marginBottom: "0.45rem",
} as const;

const summaryValueStyle = {
  color: "var(--color-text)",
  fontSize: "1.15rem",
  fontWeight: 700,
  lineHeight: 1.25,
} as const;

const summaryCopyStyle = {
  margin: "0.45rem 0 0",
  color: "var(--color-text-muted)",
  lineHeight: 1.5,
} as const;

const controlLabelStyle = {
  display: "block",
  color: "var(--color-text)",
  fontSize: "0.82rem",
  fontWeight: 700,
  marginBottom: "0.4rem",
} as const;

const controlNoteStyle = {
  marginTop: "0.45rem",
  color: "var(--color-text-muted)",
  fontSize: "0.78rem",
  lineHeight: 1.45,
} as const;

const controlInputStyle = {
  width: "100%",
  height: "42px",
  borderRadius: "10px",
  border: "1px solid rgba(148, 163, 184, 0.28)",
  background: "rgba(255,255,255,0.04)",
  color: "var(--color-text)",
  padding: "0 0.8rem",
  fontSize: "0.92rem",
} as const;

const modeChipStyle = (active: boolean) =>
  ({
    padding: "0.5rem 0.9rem",
    borderRadius: "999px",
    border: active ? "1px solid rgba(59, 130, 246, 0.52)" : "1px solid rgba(148, 163, 184, 0.22)",
    background: active ? "rgba(59, 130, 246, 0.18)" : "rgba(15, 23, 42, 0.55)",
    color: "var(--color-text)",
    fontWeight: 700,
    cursor: "pointer",
  }) as const;

const stackPillStyle = (active: boolean, color: string) =>
  ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "58px",
    padding: "0.26rem 0.6rem",
    borderRadius: "999px",
    border: `1px solid ${active ? color : "rgba(148, 163, 184, 0.2)"}`,
    background: active ? `${color}20` : "rgba(255,255,255,0.04)",
    color: active ? color : "var(--color-text-muted)",
    fontSize: "0.75rem",
    fontWeight: 700,
  }) as const;

const thLeft = {
  textAlign: "left" as const,
  padding: "0.85rem 1rem",
  color: "var(--color-text)",
  fontSize: "0.82rem",
} as const;

const thRight = {
  ...thLeft,
  textAlign: "right" as const,
} as const;

const emptyRowStyle = {
  padding: "2rem 1rem",
  textAlign: "center" as const,
  color: "var(--color-text-muted)",
} as const;
